/**
 * EPN-216 Employee invitation + Step 1 onboarding: wire types, mirroring the shared contract.
 * Nothing here ever carries a full SSN, bank data, another employee, or an audit user id/email.
 */

import type { TaxSetupStatus } from '../tax-settings/types';

export type OnboardingSectionSlug =
  | 'personal'
  | 'address'
  | 'work-details'
  | 'work-location'
  | 'pay-method'
  | 'pay-information'
  | 'payroll-details';

export interface OnboardingSectionDef {
  slug: OnboardingSectionSlug;
  label: string;
}

/** Figma order of the Step 1 left navigation. */
export const ONBOARDING_SECTIONS: readonly OnboardingSectionDef[] = [
  { slug: 'personal', label: 'Personal Information' },
  { slug: 'address', label: 'Home/Residence Address' },
  { slug: 'work-details', label: 'Work Details' },
  { slug: 'work-location', label: 'Work Location' },
  { slug: 'pay-method', label: 'Pay Method and Bank Details' },
  { slug: 'pay-information', label: 'Pay Information' },
  { slug: 'payroll-details', label: 'Payroll Details' },
];

/** Step 2 (Taxes & Exemptions) starts at Federal Income Tax. */
export const TAXES_PLACEHOLDER_PATH = '/employee/taxes/federal';
/** Step 3 (Upload Documents) is out of scope here: the Step 2 flow only hands off to this placeholder. */
export const DOCUMENTS_PLACEHOLDER_PATH = '/employee/documents';
export const sectionPath = (slug: OnboardingSectionSlug) => `/employee/setup/${slug}`;

// ---- Identity API (anonymous) ----

export interface InvitationCheckResponse {
  companyName: string;
  employeeFirstName: string;
  email: string;
}

export interface AcceptInvitationInput {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface AcceptInvitationResponse {
  redirectTo: string;
}

// ---- Employees API (employee principal only) ----

export interface AddressDto {
  line1: string;
  line2: string | null;
  country: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface PersonalDto {
  firstName: string;
  lastName: string;
  /** Read-only: shown, never sent. */
  email: string;
  phone: string | null;
  gender: string | null;
  dateOfBirth: string | null;
}

/** What the employee may send. Deliberately has no email, employee number, hire date or status. */
export type SavePersonalInput = Pick<PersonalDto, 'firstName' | 'lastName' | 'phone' | 'gender' | 'dateOfBirth'>;

export interface EmergencyContactDto {
  fullName: string;
  phone: string;
  relation: string;
}

export interface WorkDetailsDto {
  /** Last four digits only; the full SSN is never returned. */
  ssnLast4: string | null;
  employeeNumber: string;
  role: string | null;
  jobTitle: string | null;
  approvingOfficer: string | null;
  wcClassCode: string | null;
  hireDate: string | null;
  workLocationAcknowledgedAtUtc: string | null;
}

export type WorkLocationType = 'Primary' | 'Secondary' | 'Additional';

export interface WorkLocationHistoryRow {
  type: WorkLocationType;
  from: string | null;
  to: string;
  effectiveDate: string;
  /** A display name or null. Never a user id or email. */
  changedBy: string | null;
  dateChanged: string;
}

export type PayMethodName = 'Check' | 'DirectDeposit';

export interface PayMethodDto {
  method: PayMethodName | null;
  availableMethods: PayMethodName[];
}

export interface PayInformationDto {
  paySchedule: string | null;
  periodStartDate: string | null;
  payDay: string | null;
  hireDate: string | null;
}

export interface PayrollDetailsDto {
  department: string | null;
  discipline: string | null;
  workType: string | null;
  /** BACKEND GAP: lives in Compensation; null until a reviewed contract exists. */
  payType: string | null;
  rate: string | null;
}

export interface EmployeeSetupResponse {
  employeeId: string;
  companyName: string | null;
  personal: PersonalDto;
  emergencyContact: EmergencyContactDto | null;
  residenceAddress: AddressDto | null;
  mailingSameAsResidence: boolean;
  mailingAddress: AddressDto | null;
  /** The administrator's Work Arrangement (OnSite, Remote, Hybrid, Traveling), read-only. */
  workArrangement?: string | null;
  /**
   * The remote-employee flag ("home address is also the work location"). Employer-controlled: the employee may change it only while
   * the administrator has set the Work Arrangement to Remote (the server enforces this).
   */
  residenceIsPrimaryWorkLocation?: boolean | null;
  workDetails: WorkDetailsDto;
  workLocations: WorkLocationHistoryRow[];
  payMethod: PayMethodDto;
  payInformation: PayInformationDto;
  payrollDetails: PayrollDetailsDto;
}

export interface SaveAddressesInput {
  residence: AddressDto;
  mailingSameAsResidence: boolean;
  mailing?: AddressDto;
  /** Sent only when the arrangement is Remote. */
  residenceIsPrimaryWorkLocation?: boolean;
}

// ---- Step 2: Taxes & Exemptions (GET/PUT /api/me/employee/taxes/**) ----

export type TaxSectionSlug = 'federal' | 'state' | 'tax-settings' | 'tax-summary';

export const TAX_SECTIONS: readonly { slug: TaxSectionSlug; label: string }[] = [
  { slug: 'federal', label: 'Federal Income Tax' },
  { slug: 'state', label: 'State Income Tax' },
  { slug: 'tax-settings', label: 'Tax Settings' },
  { slug: 'tax-summary', label: 'Tax Jurisdiction Summary' },
];
export const taxSectionPath = (slug: TaxSectionSlug) => `/employee/taxes/${slug}`;

/** Filing statuses of the current (2020 or later) W-4. The wire values are the server's enum names. */
export const W4_FILING_STATUSES = [
  { value: 3, label: 'Single or Married filing separately' },
  { value: 4, label: 'Married filing jointly' },
  { value: 5, label: 'Head of household' },
] as const;

export interface FederalDraftDto {
  filingStatus: string;
  multipleJobs: boolean | null;
  dependentsAmount: number | null;
  otherIncome: number | null;
  deductions: number | null;
  extraWithholding: number | null;
  mostRecentWithholdingAmount: number | null;
}

/** An existing (administrator- or import-recorded) 2019-or-earlier W-4: shown read-only, never editable here. */
export interface HistoricalW4Dto {
  formVersion: string;
  filingStatus: string;
  allowances: number | null;
  extraWithholding: number | null;
  effectiveFrom: string;
}

export interface FederalSectionDto {
  status: 'NotStarted' | 'DraftNotSubmitted';
  draft: FederalDraftDto | null;
  historical: HistoricalW4Dto | null;
  /** False today: there is no signed-certificate capability, so a W-4 is never "submitted" from here. */
  signatureCapabilityAvailable: boolean;
}

export interface StateOptionDto {
  code: string;
  name: string;
  source: 'Work' | 'Residence';
  /** `NotYetSupported`: a territory. EPN has no state-election form for it, so it has no filing statuses and nothing is saved for it. */
  ruleKind: 'NoWageIncomeTax' | 'PendingSmeConfiguration' | 'NotYetSupported';
  filingStatuses: string[];
  usesAllowances: boolean;
}

export interface StateDraftDto {
  stateCode: string | null;
  filingStatus: string | null;
  allowances: number | null;
  mostRecentWithholdingAmount: number | null;
}

export interface StateSectionDto {
  status: 'NotStarted' | 'DraftNotSubmitted';
  options: StateOptionDto[];
  draft: StateDraftDto | null;
  ruleNote: string | null;
}

/** `Verified` = from real data; `Unknown` / `PendingReview` are never shown as if they were values; `NotApplicable` is a real answer. */
export interface JurisdictionValueDto {
  value: string | null;
  status: 'Verified' | 'Unknown' | 'PendingReview' | 'NotApplicable';
  note: string | null;
}

export interface AdditionalWithholdingDto {
  state: string | null;
  value: number | null;
  type: string | null;
}

export type ReciprocityStatus =
  | 'NotApplicable'
  | 'Unknown'
  | 'EligibleCertificateNeeded'
  | 'SubmittedPendingReview'
  | 'Approved'
  | 'Rejected'
  | 'NeedsReview'
  // The reciprocity lifecycle (contract section 3). A server that does not send them yet simply never produces these values.
  | 'Expired'
  | 'Revoked';

export interface ReciprocityDto {
  status: ReciprocityStatus;
  reason: string | null;
  workState: string | null;
  residenceState: string | null;
  requiredForm: string | null;
  requiredFormDescription: string | null;
  effectiveFrom: string | null;
  /** The approval's last day; null / absent = no expiry recorded. Absent on a server that does not send it yet. */
  expiresOn?: string | null;
  submittedAtUtc: string | null;
  reviewNote: string | null;
  canSubmit: boolean;
  canWithdraw: boolean;
}

export interface TaxSettingsDto {
  workState: JurisdictionValueDto;
  workLocal: JurisdictionValueDto;
  residenceState: JurisdictionValueDto;
  residenceLocal: JurisdictionValueDto;
  additionalWithholding: AdditionalWithholdingDto;
  sui: { status: 'NotSet'; tooltip: string };
  reciprocity: ReciprocityDto;
}

export interface MultiStateDto {
  status: 'NotApplicable' | 'ReviewRequired' | 'Unresolved';
  states: string[];
  message: string;
  unresolved: string[];
}

export interface TaxSummaryDto {
  federal: string;
  workState: JurisdictionValueDto;
  residenceState: JurisdictionValueDto;
  reciprocityLabel: string;
  workLocal: JurisdictionValueDto;
  residenceLocal: JurisdictionValueDto;
  sui: JurisdictionValueDto;
  sdiPfl: JurisdictionValueDto;
  multiState: MultiStateDto;
  payrollReady: boolean;
  payrollReadinessNote: string;
}

export interface TaxSetupResponse {
  federal: FederalSectionDto;
  state: StateSectionDto;
  settings: TaxSettingsDto;
  summary: TaxSummaryDto;
  referenceDataVersion: string;
  /** DraftPendingSmeApproval until a tax specialist approves the reference data. */
  referenceDataStatus: string;
  /** The derived tax setup status for the employee view (informational; never persisted). Absent or null on a server that does not send it yet. */
  taxSetup?: TaxSetupStatus | null;
}

/** What the employee may send for the federal W-4. Always the current (2020 or later) form; a new 2019 form is refused by the server. */
export interface SaveFederalW4Input {
  formVersion: 'Y2020OrLater';
  filingStatus: number;
  multipleJobs: boolean;
  dependentsAmount: number;
  otherIncome: number;
  deductions: number;
  extraWithholding: number;
  mostRecentWithholdingAmount?: number;
}

/** What the employee may send for state tax. There is deliberately no work state, residence state, SUI or reciprocity approval here. */
export interface SaveStateTaxInput {
  stateCode: string;
  filingStatus?: string;
  allowances?: number;
  mostRecentWithholdingAmount?: number;
  additionalWithholdingState?: string;
  additionalWithholdingValue?: number;
  additionalWithholdingType?: 'Amount';
}

/** GET /api/me/employee/submission and POST /api/me/employee/submit. `missing` holds the section keys still to complete. Documents are not
 * required yet (the Upload Documents step does not exist), which `documentsRequired: false` says out loud. */
export interface EmployeeSubmission {
  submitted: boolean;
  submittedAtUtc: string | null;
  canSubmit: boolean;
  missing: OnboardingMissingKey[];
  documentsRequired: boolean;
}

export type OnboardingMissingKey = 'personal' | 'address' | 'workLocation' | 'payMethod' | 'federalTax' | 'stateTax';

/** Where each still-missing section is completed, in the order the employee works through them. */
export const MISSING_SECTION_TARGETS: Record<OnboardingMissingKey, { label: string; path: string }> = {
  personal: { label: 'Personal Information', path: '/employee/setup/personal' },
  address: { label: 'Home/Residence Address', path: '/employee/setup/address' },
  workLocation: { label: 'Work Location', path: '/employee/setup/work-location' },
  payMethod: { label: 'Pay Method and Bank Details', path: '/employee/setup/pay-method' },
  federalTax: { label: 'Federal Income Tax', path: '/employee/taxes/federal' },
  stateTax: { label: 'State Income Tax', path: '/employee/taxes/state' },
};
